import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import { GetProjectThumbnails } from '../../../api';
import Spinner from '../../Atoms/Spinner';
import styled from 'styled-components';

const Container = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 20px 0 30px 0;
    padding: 20px;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 12px;
    border: 1px solid #dee2e6;
`;

const StatsInfo = styled.div`
    font-size: 16px;
    color: #555;
    font-weight: 600;
    text-align: center;

    .main-title {
        font-size: 24px;
        color: #f39c12;
        font-weight: 700;
        margin-bottom: 10px;
    }

    .event-info {
        font-size: 18px;
        color: #333;
        margin-bottom: 15px;
    }
`;

const BackButton = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white !important;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.3s ease;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(243, 156, 18, 0.3);

    &:hover {
        background: linear-gradient(135deg, #e67e22, #d35400);
        color: white !important;
        transform: translateX(-2px);
        box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
    }
`;

const ProjectGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 25px;
    margin-bottom: 40px;
    padding: 20px 10px;
`;

const ProjectCard = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 24px;
    background: white;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    position: relative;
    overflow: visible;

    &:hover {
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
        border-color: #f39c12;
    }
`;

const ProjectImage = styled.div`
    width: 100%;
    height: 220px;
    background-size: cover;
    background-position: center;
    background-color: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 18px;
    border: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ccc;
    font-size: 48px;
`;

const ProjectName = styled.h3`
    margin: 0 0 12px 0;
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.3;
`;

const DesignerName = styled.p`
    margin: 0 0 16px 0;
    color: #666;
    font-size: 15px;
    font-weight: 500;
`;

const RankingBadge = styled.div`
    position: absolute;
    top: -12px;
    right: -12px;
    background: ${props => {
        if (props.rank <= 10) return 'linear-gradient(135deg, #ffd700, #ffb300)';
        if (props.rank <= 50) return 'linear-gradient(135deg, #c0c0c0, #999999)';
        return 'linear-gradient(135deg, #cd7f32, #b87333)';
    }};
    color: white;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 14px;
    box-shadow: 0 2px 8px ${props => {
        if (props.rank <= 10) return 'rgba(255, 215, 0, 0.5)';
        if (props.rank <= 50) return 'rgba(192, 192, 192, 0.5)';
        return 'rgba(205, 127, 50, 0.5)';
    }};
    border: 2px solid ${props => {
        if (props.rank <= 10) return '#ffb300';
        if (props.rank <= 50) return '#808080';
        return '#a0522d';
    }};
    z-index: 10;
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    margin-top: 8px;
    margin-right: 6px;
`;

const ViewButton = styled(Link)`
    display: inline-block;
    padding: 12px 20px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
    text-decoration: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(243, 156, 18, 0.3);
    margin-right: 10px;
    margin-bottom: 10px;

    &:hover {
        background: linear-gradient(135deg, #e67e22, #d35400);
        color: white;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
    }
`;

const PAST_EDITION_IDS = {
    'ec25d885-3dcf-455c-93b2-26041cb842e4': '1ère édition',
    '417b2020-721a-4c55-bd9f-d86a913f26ad': '2ème édition',
    'a3b7c9d1-e5f6-4a2b-8c3d-1e5f6a2b8c3d': '3ème édition'
};

const FD100PastEditionGallery = () => {
    const [searchParams] = useSearchParams();
    const eventId = searchParams.get('id');
    const [loading, setLoading] = useState(true);
    const [winners, setWinners] = useState([]);
    const [eventInfo, setEventInfo] = useState(null);
    const [thumbnails, setThumbnails] = useState({});

    const editionName = PAST_EDITION_IDS[eventId] || 'Edition inconnue';

    useEffect(() => {
        if (eventId && PAST_EDITION_IDS[eventId]) {
            loadWinners();
        } else {
            setLoading(false);
        }
    }, [eventId]);

    const loadWinners = async () => {
        setLoading(true);
        try {
            // Fetch event info
            const { data: eventData } = await supabase
                .from('events')
                .select('*')
                .eq('id', eventId)
                .single();

            if (eventData) setEventInfo(eventData);

            // Fetch winners
            const { data: winnersData, error } = await supabase
                .from('winners')
                .select('*')
                .eq('event_id', eventId)
                .order('company_name', { ascending: true });

            if (error) {
                console.error('Error fetching winners:', error);
            } else {
                setWinners(winnersData || []);

                // Fetch thumbnails for linked projects
                const linkedItems = (winnersData || [])
                    .filter(w => w.project_id && w.company_id)
                    .map(w => ({ project_id: w.project_id, company_id: w.company_id }));

                if (linkedItems.length > 0) {
                    try {
                        const thumbResponse = await GetProjectThumbnails(linkedItems);
                        const data = thumbResponse.data || {};
                        setThumbnails(data.thumbnails || data);
                    } catch (thumbErr) {
                        console.error('Error fetching thumbnails:', thumbErr);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading winners:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!eventId || !PAST_EDITION_IDS[eventId]) {
        return (
            <Container>
                <BackButton to="/admin/events">← Retour aux événements</BackButton>
                <p>Edition non trouvée.</p>
            </Container>
        );
    }

    if (loading) return <Spinner />;

    const onPlatformCount = winners.filter(w => w.user_id).length;
    const withProjectCount = winners.filter(w => w.project_id).length;

    return (
        <Container>
            <BackButton to="/admin/events">
                ← Retour aux événements
            </BackButton>

            <HeaderSection>
                <StatsInfo>
                    <div className="main-title">FD100 {editionName} - Lauréats</div>
                    <div className="event-info">
                        {eventInfo?.title_fr || `Le French Design 100 - ${editionName}`}
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                        {winners.length} lauréats | {onPlatformCount} sur la plateforme | {withProjectCount} projets dans l'annuaire
                    </div>
                </StatsInfo>
            </HeaderSection>

            <ProjectGrid>
                {winners.map((winner, idx) => (
                    <ProjectCard key={winner.id || idx}>
                        <RankingBadge rank={idx + 1}>#{idx + 1}</RankingBadge>
                        <ProjectImage style={{
                            backgroundImage: thumbnails[winner.project_id]
                                ? `url(${process.env.REACT_APP_MEDIA_BASE_URL}${thumbnails[winner.project_id]})`
                                : 'none'
                        }}>
                            {!thumbnails[winner.project_id] && '?'}
                        </ProjectImage>
                        <ProjectName>{winner.project_name || 'Sans nom'}</ProjectName>
                        <DesignerName>
                            {winner.company_name || `${winner.first_name || ''} ${winner.last_name || ''}`.trim() || 'Inconnu'}
                        </DesignerName>
                        <div>
                            {winner.user_id ? (
                                <StatusBadge style={{ background: '#d4edda', color: '#155724' }}>
                                    ✓ Sur la plateforme
                                </StatusBadge>
                            ) : (
                                <StatusBadge style={{ background: '#fff3cd', color: '#856404' }}>
                                    ○ Pas encore inscrit
                                </StatusBadge>
                            )}
                            {winner.project_id ? (
                                <StatusBadge style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                                    ✓ Projet lié
                                </StatusBadge>
                            ) : (
                                <StatusBadge style={{ background: '#f8d7da', color: '#721c24' }}>
                                    ○ Projet non lié
                                </StatusBadge>
                            )}
                        </div>
                        <div style={{ marginTop: 12 }}>
                            {winner.project_id && winner.company_id && (
                                <ViewButton
                                    to={`/project?pid=${winner.project_id}&cid=${winner.company_id}`}
                                    target="_blank"
                                >
                                    Fiche projet
                                </ViewButton>
                            )}
                            {winner.company_id && (
                                <ViewButton
                                    to={`/company?id=${winner.company_id}`}
                                    target="_blank"
                                    style={{ background: 'linear-gradient(135deg, #6c757d, #495057)' }}
                                >
                                    Fiche agence
                                </ViewButton>
                            )}
                        </div>
                    </ProjectCard>
                ))}
            </ProjectGrid>
        </Container>
    );
};

export default FD100PastEditionGallery;
